import { createPropertySelectors, createSelector } from '@ngxs/store';
import _ from 'lodash';
import { denormalize } from 'normalizr';

import { TableHelper } from 'src/app/helpers';
import {
  NormalizedEntities,
  ReservationTable,
  StateObject,
  Table,
  tablesArraySchema
} from 'src/app/models';
import { AppSelectors } from 'src/app/store/app/app.selectors';
import { LocationSelectors } from 'src/app/store/location/location.selectors';
import { SeatsStateSelectors } from 'src/app/store/seats/seats.state.selectors';
import { TablesState } from 'src/app/store/tables/tables.state';
import { TableStateModel } from 'src/app/store/tables/tables.state.model';
import { UtilsSelectors } from 'src/app/store/utils.selectors';

export class TablesStateSelectors {
  static slices = createPropertySelectors<TableStateModel>(TablesState);

  private static tablesData = createSelector(
    [TablesStateSelectors.slices.tables],
    (tables) => tables
  );

  static selectedTableData = createSelector(
    [TablesStateSelectors.slices.selectedTable],
    (selectedTable) => selectedTable
  );

  static getSelectedTableId = createSelector(
    [TablesStateSelectors.selectedTableData],
    (selectedTable) => selectedTable.data
  );

  static getNewTables = createSelector(
    [TablesStateSelectors.slices.newTables],
    (newTables) => newTables
  );

  static newTablesData = createSelector(
    [TablesStateSelectors.getNewTables],
    (newTables: StateObject<Table[]>) => newTables.data
  );

  static getNewTablesLoaded = createSelector(
    [TablesStateSelectors.getNewTables],
    (newTables) => newTables.isLoaded
  );

  static getTablesFilteredByOperator = createSelector(
    [TablesStateSelectors.newTablesData, AppSelectors.getOperator],
    (tables, operator) => TableHelper.filterTablesByPermission(tables, operator)
  );

  static getTablesForFloorplan = createSelector(
    [TablesStateSelectors.getTablesFilteredByOperator],
    (tables) =>
      tables.map((prevTable) =>
        TableHelper.formatFloorplanTable(
          TableHelper.formatTableWithValidSeat(prevTable)
        )
      )
  );

  static getTableListingData = createSelector(
    [TablesStateSelectors.slices.posListingTables],
    (posListingTables) => posListingTables
  );

  static resetSelectedTableIds = createSelector(
    [TablesStateSelectors.slices.resetSelectedTableIds],
    (resetSelectedTableIds) => resetSelectedTableIds
  );

  static isTableSelected = createSelector(
    [TablesStateSelectors.getSelectedTableId],
    (selectedTableId) => !_.isNil(selectedTableId)
  );

  static getSelectedTable = createSelector(
    [
      TablesStateSelectors.getSelectedTableId,
      TablesStateSelectors.newTablesData
    ],
    (selectedTableId, newTables) =>
      newTables.find((i) => i.id === selectedTableId)
  );

  static getReservationsOfSelectedTable = createSelector(
    [TablesStateSelectors.getSelectedTable],
    (selectedTable) => selectedTable.reservations
  );

  static getActiveReservationOfSelectedTable = createSelector(
    [
      TablesStateSelectors.getReservationsOfSelectedTable,
      LocationSelectors.reservation_meal_configurations
    ],
    (reservations, reservationMealConfigurations) =>
      TableHelper.getCurrentReservationOfTable(
        reservations,
        reservationMealConfigurations
      )
  );

  static getReservationDinersOfSelectedTable = createSelector(
    [
      TablesStateSelectors.getActiveReservationOfSelectedTable,
      UtilsSelectors.getSelectedTable
    ],
    (activeReservation, table) => {
      const assignedDiners: number[] = _.uniq(
        _.compact(table.seats.map((i) => i.diner_id))
      );
      return activeReservation.reservation_diners.filter((diner) => {
        const assignedDinerIdx = assignedDiners.indexOf(diner.diner_id);
        if (assignedDinerIdx > -1) {
          assignedDiners.splice(assignedDinerIdx, 1);
        }
        return assignedDinerIdx === -1;
      });
    }
  );

  static getTablesWithoutSeatDataHostMode = createSelector(
    [TablesStateSelectors.tablesData, AppSelectors.getOperator],
    (tablesData, operator) => {
      if (tablesData.byId === null) {
        return null;
      }

      const entities: NormalizedEntities = {
        tables: tablesData.byId
      };
      const orderedTables: Table[] = _.orderBy(
        denormalize(tablesData.allIds, tablesArraySchema, entities) || [],
        'sort'
      );

      return (
        (TableHelper.filterTablesByPermission(
          orderedTables,
          operator
        ) as Table[]) || []
      );
    }
  );

  static getDiagramTables = createSelector(
    [TablesStateSelectors.tablesData, SeatsStateSelectors.seatsById],
    (tablesData, seats) => {
      const entities: NormalizedEntities = {
        tables: tablesData.byId,
        seats
      };
      const tables: Table[] = _.orderBy(
        denormalize(tablesData.allIds, tablesArraySchema, entities) || [],
        'sort'
      );
      return tables;
    }
  );

  static getTableManagementFloorPlan = createSelector(
    [TablesStateSelectors.slices.selectedTableManagementFloorPlan],
    (tables) => TableHelper.filterValidSeatsForTablesManagement(tables)
  );

  static isSelectedTableLoaded = createSelector(
    [TablesStateSelectors.selectedTableData],
    (selectedTable) => selectedTable.isLoaded
  );

  static isSelectedTableLoading = createSelector(
    [TablesStateSelectors.selectedTableData],
    (selectedTable) => selectedTable.isProcessing
  );

  static getFormattedTables = createSelector(
    [
      TablesStateSelectors.tablesData,
      SeatsStateSelectors.seatsById,
      AppSelectors.getOperator
    ],
    (tables, normalizedSeats, operator) =>
      TableHelper.filterValidSeatsByPermission(
        tables,
        normalizedSeats,
        operator,
        true
      )
  );

  static showTablesList = createSelector(
    [TablesStateSelectors.slices.showTablesList],
    (showTablesList) => showTablesList
  );

  static selectedTableManagementEditingStatus = createSelector(
    [TablesStateSelectors.slices.selectedTableManagementEditingStatus],
    (selectedTableManagementEditingStatus) =>
      selectedTableManagementEditingStatus
  );

  static getAttachDetailIntoReservationTablesFunc = createSelector(
    [TablesStateSelectors.getDiagramTables],
    (tables) => (reservationTable: ReservationTable[]) =>
      reservationTable.map(
        (selectedTable) =>
          <ReservationTable>{
            ...selectedTable,
            table_detail: tables.find(
              (table) => selectedTable.table_id === table.id
            )
          }
      )
  );

  static isLoadingSelectedTable = createSelector(
    [TablesStateSelectors.slices.selectedTable],
    (selected) => !!selected.isProcessing
  );
}
